import React, { useState, useEffect, useRef, useCallback } from 'react';
import { toast } from 'react-hot-toast';
import {
  ClockIcon,
  ArrowPathIcon,
  QuestionMarkCircleIcon,
  ClipboardDocumentListIcon,
  ChevronUpIcon,
  ChevronDownIcon,
  TrashIcon,
} from '@heroicons/react/24/outline';
import {
  CheckCircleIcon,
  XCircleIcon,
  StopCircleIcon,
} from '@heroicons/react/24/solid';
import apiService from '../services/api';
import { BuildStatus } from '../types/api';
import { getApiErrorMessage } from '../constants/messages';
import Logger from '../utils/logger';
import { useTranslation } from 'react-i18next';

interface BuildProgressModalProps {
  isOpen: boolean;
  buildId: string | null;
  onClose: () => void;
  onSuccess: (imageUrl: string, imageTag: string) => void;
  onError: (error: string) => void;
  onDelete?: (buildId: string) => void;
  onStatusChange?: (buildId: string, status: string) => void;
}

const BuildProgressModal: React.FC<BuildProgressModalProps> = ({
  isOpen,
  buildId,
  onClose,
  onSuccess,
  onError,
  onDelete,
  onStatusChange
}) => {
  const { t } = (useTranslation as any)('builds');
  const [buildStatus, setBuildStatus] = useState<BuildStatus | null>(null);
  const [logs, setLogs] = useState<string>('');
  const [logsDict, setLogsDict] = useState<Record<string, string[]>>({});
  const [showLogs, setShowLogs] = useState(true);
  const [activeTab, setActiveTab] = useState<string | null>(null);
  const [scanResults, setScanResults] = useState<{status: string; vulnerabilities: number; cves: any[]} | null>(null);
  const [isCancelling, setIsCancelling] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [isRetrying, setIsRetrying] = useState(false);
  const [failedAttempts, setFailedAttempts] = useState(0);
  const [elapsedTime, setElapsedTime] = useState<number>(0);
  const pollingIntervalRef = useRef<NodeJS.Timeout | null>(null);
  const failureTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const durationTimerRef = useRef<NodeJS.Timeout | null>(null);
  const isFirstPollRef = useRef(true);

  // Format task names for display:
  // - kpack: les etapes arrivent sous leur nom de conteneur ("prepare" → "Prepare"),
  //   lues sur le pod. La branche qui retirait un prefixe "kpack-" a ete supprimee
  //   avec la liste codee en dur qui le fabriquait (API, 2026-10-11).
  // - Tekton: "build-1770063296-master-20260202-fetch-source" → "Fetch-source"
  const formatTaskName = useCallback((name: string): string => {
    if (!name) return name;

    // Handle Tekton task names with build ID prefix
    // Pattern: build-{timestamp}-{branch}-{YYYYMMDD}-{task-name}
    // Branch can contain hyphens, so we anchor on the 8-digit date
    // e.g., "Build-1770499581-feature-saas-b2b-pivot-20260207-fetch-source" → "Fetch-source"
    const tektonMatch = name.match(/^build-\d+-.*?-\d{8}-(.+)$/i);
    if (tektonMatch && tektonMatch[1]) {
      const taskName = tektonMatch[1];
      return taskName.charAt(0).toUpperCase() + taskName.slice(1);
    }

    // Fallback: return original name with first letter capitalized
    return name.charAt(0).toUpperCase() + name.slice(1);
  }, []);

  const formatLogsFromDict = useCallback((rawLogsDict: Record<string, string[]>): string => {
    if (!rawLogsDict || Object.keys(rawLogsDict).length === 0) {
      return 'Aucun log disponible pour le moment...';
    }

    // Store the raw dict for tab filtering
    setLogsDict(rawLogsDict);

    // Scan tab: hide logs (CVE table shown instead)
    if (activeTab && (activeTab.includes('scan') || activeTab.includes('security'))) {
      return '';
    }

    // If a tab is active, show only that task's logs
    if (activeTab) {
      const taskLogs = Object.entries(rawLogsDict).find(([name]) =>
        name.toLowerCase().includes(activeTab.toLowerCase()) ||
        formatTaskName(name).toLowerCase() === activeTab.toLowerCase()
      );
      if (taskLogs) {
        return taskLogs[1].join('\n');
      }
    }

    // Default: show all logs
    let formattedLogs = '';
    Object.entries(rawLogsDict).forEach(([taskName, logLines]) => {
      formattedLogs += `\n=== ${formatTaskName(taskName)} ===\n`;
      logLines.forEach(line => {
        formattedLogs += line + '\n';
      });
    });

    return formattedLogs;
  }, [formatTaskName, activeTab]);

  // Re-filter logs when activeTab changes
  useEffect(() => {
    if (Object.keys(logsDict).length > 0) {
      // Scan tab: hide logs (CVE table is shown instead)
      if (activeTab && (activeTab.includes('scan') || activeTab.includes('security'))) {
        setLogs('');
        return;
      }
      // Other tabs: filter to that task's logs
      if (activeTab) {
        const taskLogs = Object.entries(logsDict).find(([name]) =>
          name.toLowerCase().includes(activeTab.toLowerCase()) ||
          formatTaskName(name).toLowerCase() === activeTab.toLowerCase()
        );
        if (taskLogs) {
          setLogs(taskLogs[1].join('\n'));
          return;
        }
      }
      // Default (no tab / "Tous"): show all logs
      let formattedLogs = '';
      Object.entries(logsDict).forEach(([taskName, logLines]) => {
        formattedLogs += `\n=== ${formatTaskName(taskName)} ===\n`;
        logLines.forEach(line => {
          formattedLogs += line + '\n';
        });
      });
      setLogs(formattedLogs);
    }
  }, [activeTab, logsDict, formatTaskName]);

  useEffect(() => {
    if (isOpen && buildId) {
      setFailedAttempts(0);
      setElapsedTime(0);
      setScanResults(null);
      setActiveTab(null);
      setLogsDict({});
      setLogs('');
      setBuildStatus(null);
      isFirstPollRef.current = true;
      startPolling();
    }

    return () => {
      stopPolling();
      if (failureTimeoutRef.current) {
        clearTimeout(failureTimeoutRef.current);
      }
      if (durationTimerRef.current) {
        clearInterval(durationTimerRef.current);
      }
    };
  }, [isOpen, buildId]);

  // Helper to get start time (supports both field names from API)
  const getStartTime = (): string | undefined => {
    return buildStatus?.started_at || buildStatus?.start_time;
  };

  // Effect for real-time duration tracking
  useEffect(() => {
    // Clear any existing timer
    if (durationTimerRef.current) {
      clearInterval(durationTimerRef.current);
      durationTimerRef.current = null;
    }

    const startTime = getStartTime();

    if (buildStatus?.status === 'running' && startTime) {
      const updateElapsed = () => {
        const start = new Date(startTime).getTime();
        const now = Date.now();
        setElapsedTime(Math.floor((now - start) / 1000));
      };

      updateElapsed(); // Immediate update
      durationTimerRef.current = setInterval(updateElapsed, 1000);
    } else if (buildStatus?.duration_seconds !== undefined) {
      setElapsedTime(buildStatus.duration_seconds);
    } else if (startTime && (buildStatus?.completed_at || buildStatus?.completion_time)) {
      // Calculate duration from start and completion times
      const start = new Date(startTime).getTime();
      const end = new Date(buildStatus?.completed_at || buildStatus?.completion_time!).getTime();
      setElapsedTime(Math.floor((end - start) / 1000));
    }

    return () => {
      if (durationTimerRef.current) {
        clearInterval(durationTimerRef.current);
      }
    };
  }, [buildStatus?.status, buildStatus?.started_at, buildStatus?.start_time, buildStatus?.duration_seconds, buildStatus?.completed_at, buildStatus?.completion_time]);

  const startPolling = () => {
    if (!buildId) return;

    // Poll status toutes les 3 secondes
    const pollStatus = async () => {
      try {
        const status = await apiService.getBuildStatus(buildId);
        setBuildStatus(status);

        // Reset failed attempts on successful API call
        setFailedAttempts(0);

        // Clear any pending failure timeout
        if (failureTimeoutRef.current) {
          clearTimeout(failureTimeoutRef.current);
        }

        // Notify parent of status change
        if (onStatusChange) {
          onStatusChange(buildId, status.status);
        }

        // Toujours charger les logs (pas juste si showLogs est true)
        try {
          const logsData = await apiService.getBuildLogs(buildId);
          const formattedLogs = formatLogsFromDict(logsData.logs);
          setLogs(formattedLogs);
        } catch (logsError) {
          Logger.warn('Impossible de charger les logs:', logsError);
          // Continue même si les logs ne se chargent pas
        }

        // Gérer les états terminaux
        const wasFirstPoll = isFirstPollRef.current;
        isFirstPollRef.current = false;

        if (status.status === 'succeeded' || status.status === 'success') {
          stopPolling();
          // Ne pas afficher le toast si le build était déjà terminé à l'ouverture
          if (!wasFirstPoll) {
            toast.success('Build terminé avec succès !');
          }
          if (status.image_url && status.image_tag) {
            onSuccess(status.image_url, status.image_tag);
          }
        } else if (status.status === 'failed') {
          // NE PAS fermer immédiatement - garder le modal ouvert pour voir les logs
          // Attendre 60 secondes avant de vraiment considérer comme échoué
          if (!failureTimeoutRef.current) {
            Logger.warn('Build marqué comme échoué, vérification dans 60s...');
            failureTimeoutRef.current = setTimeout(() => {
              // Après 60s, si toujours failed, alors vraiment échoué
              if (buildStatus?.status === 'failed') {
                stopPolling();
                if (!wasFirstPoll) {
                  const errorMsg = status.error_message || 'Le build a échoué';
                  toast.error(errorMsg);
                  onError(errorMsg);
                }
              }
            }, 60000);
          }
        }
      } catch (error: unknown) {
        Logger.error('Erreur lors du polling:', error);
        setFailedAttempts(prev => prev + 1);

        // Si plus de 10 tentatives échouent (30+ secondes), afficher un avertissement
        if (failedAttempts >= 10) {
          toast.error('Impossible de se connecter à l\'API. Vérifiez votre connexion.');
        }
      }
    };

    // Premier appel immédiat
    pollStatus();

    // Puis polling régulier
    pollingIntervalRef.current = setInterval(pollStatus, 3000);
  };

  const stopPolling = () => {
    if (pollingIntervalRef.current) {
      clearInterval(pollingIntervalRef.current);
      pollingIntervalRef.current = null;
    }
  };


  const getStatusColor = (status: string): string => {
    switch (status) {
      case 'pending':
        return 'bg-surface-3 text-text-secondary';
      case 'running':
        return 'bg-blue-500/12 text-blue-400';
      case 'success':
      case 'succeeded':
        return 'bg-green-500/12 text-green-400';
      case 'failed':
        return 'bg-red-500/12 text-red-400';
      case 'cancelled':
        return 'bg-yellow-500/12 text-yellow-400';
      default:
        return 'bg-surface-3 text-text-secondary';
    }
  };

  const getStatusIcon = (status: string): React.ReactNode => {
    switch (status) {
      case 'pending':
        return <ClockIcon className="w-5 h-5" />;
      case 'running':
        return <ArrowPathIcon className="w-5 h-5" />;
      case 'success':
      case 'succeeded':
        return <CheckCircleIcon className="w-5 h-5" />;
      case 'failed':
        return <XCircleIcon className="w-5 h-5" />;
      case 'cancelled':
        return <StopCircleIcon className="w-5 h-5" />;
      default:
        return <QuestionMarkCircleIcon className="w-5 h-5" />;
    }
  };

  const getStatusText = (status: string): string => {
    switch (status) {
      case 'pending':
        return 'En attente';
      case 'running':
        return 'En cours';
      case 'success':
      case 'succeeded':
        return 'Réussi';
      case 'failed':
        return 'Échoué';
      case 'cancelled':
        return 'Arrêté';
      default:
        return 'Inconnu';
    }
  };

  const getProgressPercentage = (): number => {
    if (!buildStatus) return 0;

    // Terminal states
    if (['success', 'succeeded', 'failed', 'cancelled'].includes(buildStatus.status)) {
      return 100;
    }

    // If pending and no stages/tasks
    if (buildStatus.status === 'pending') {
      return 5;
    }

    // Calculate progress based on tasks (from Tekton) or stages
    const tasks = buildStatus.tasks || [];
    const stages = buildStatus.stages || [];
    const stepsArray = tasks.length > 0 ? tasks : stages;

    if (stepsArray.length === 0) {
      // Fallback if no stages/tasks: time-based interpolation
      const startTime = getStartTime();
      if (startTime) {
        const elapsed = (Date.now() - new Date(startTime).getTime()) / 1000;
        // Logarithmic progression: rises fast at beginning, slows down
        // Max 95% to leave room for "succeeded"
        return Math.min(95, Math.round(10 + 85 * (1 - Math.exp(-elapsed / 180))));
      }
      return 15;
    }

    // Calculate based on completed steps
    const totalSteps = stepsArray.length;
    let completedSteps = 0;
    let runningStepProgress = 0;

    stepsArray.forEach((step) => {
      if (step.status === 'succeeded' || step.status === 'success') {
        completedSteps++;
      } else if (step.status === 'running') {
        // Running step counts for 0.5
        runningStepProgress = 0.5;
      }
    });

    const progress = ((completedSteps + runningStepProgress) / totalSteps) * 100;
    // Keep between 5% and 95%
    return Math.max(5, Math.min(95, Math.round(progress)));
  };

  const formatDuration = (seconds?: number): string => {
    if (seconds === undefined || seconds === null) return '-';
    if (seconds === 0) return '0s';

    const minutes = Math.floor(seconds / 60);
    const secs = seconds % 60;

    if (minutes > 0) {
      return `${minutes}m ${secs}s`;
    }
    return `${secs}s`;
  };

  const handleCancelBuild = async () => {
    if (!buildId) return;

    if (!window.confirm('Êtes-vous sûr de vouloir arrêter ce build ? Il continuera à apparaître dans l\'historique.')) {
      return;
    }

    try {
      setIsCancelling(true);
      await apiService.cancelBuild(buildId);
      toast.success('Build arrêté avec succès');
      // Polling continuera pour montrer le statut "cancelled"
    } catch (error: unknown) {
      Logger.error('Erreur lors de l\'arrêt du build:', error);
      toast.error(getApiErrorMessage(error) || 'Erreur lors de l\'arrêt du build');
    } finally {
      setIsCancelling(false);
    }
  };

  const handleDeleteBuild = async () => {
    if (!buildId) return;

    if (!window.confirm('Êtes-vous SÛR de vouloir COMPLÈTEMENT SUPPRIMER ce build ? Il disparaîtra de l\'historique.')) {
      return;
    }

    try {
      setIsDeleting(true);
      await apiService.deleteBuild(buildId);
      toast.success('Build supprimé complètement avec succès');
      stopPolling();

      // Notify parent that build was deleted
      if (onDelete) {
        onDelete(buildId);
      }

      onClose();
    } catch (error: unknown) {
      Logger.error('Erreur lors de la suppression du build:', error);
      toast.error(getApiErrorMessage(error) || 'Erreur lors de la suppression du build');
    } finally {
      setIsDeleting(false);
    }
  };

  const handleRetryBuild = async () => {
    if (!buildId) return;

    try {
      setIsRetrying(true);
      const response = await apiService.retryBuild(buildId);

      if (response.success && response.build_id) {
        toast.success('Build relancé avec succès !');
        // Note: The retry endpoint currently returns an error (not implemented with DB)
        // But we show success message if the API call succeeds
      }
    } catch (error: unknown) {
      Logger.error('Erreur lors de la relance du build:', error);
      toast.error(getApiErrorMessage(error) || 'Erreur lors de la relance du build');
    } finally {
      setIsRetrying(false);
    }
  };

  if (!isOpen || !buildId) return null;

  return (
    <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50 flex items-center justify-center" role="dialog" aria-modal="true" aria-labelledby="build-progress-title">
      <div className="relative bg-surface-2 rounded-lg shadow-xl max-w-4xl w-full mx-4 max-h-[90vh] flex flex-col">
        {/* Header */}
        <div className="flex justify-between items-center p-6 border-b border-border-1">
          <div>
            <h3 id="build-progress-title" className="text-2xl font-bold text-text-primary flex items-center">
              Build Progress
              {buildStatus && (
                <span className={`ml-3 inline-flex items-center gap-1 px-3 py-1 rounded-full text-xs font-medium ${getStatusColor(buildStatus.status)}`}>
                  {getStatusIcon(buildStatus.status)} {getStatusText(buildStatus.status)}
                </span>
              )}
            </h3>
            <p className="text-sm text-text-tertiary mt-1">
              Build ID: <span className="font-mono">{buildId}</span>
            </p>
          </div>
          <button
            onClick={onClose}
            aria-label={buildStatus?.status === 'running' ? 'Fermer (le build continuera en arriere-plan)' : 'Fermer'}
            className="text-text-quaternary hover:text-text-secondary transition-colors focus:outline-none focus:ring-2 focus:ring-accent-500 focus:ring-offset-2 rounded"
          >
            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        {/* Content - wrapper with scroll for the whole section */}
        <div className="flex-1 overflow-y-auto p-6 space-y-6">
          {/* Progress Bar */}
          <div>
            <div className="flex justify-between text-sm text-text-secondary mb-2">
              <span>Progression</span>
              <span>{getProgressPercentage()}%</span>
            </div>
            <div className="w-full bg-surface-4 rounded-full h-3 overflow-hidden">
              <div
                className={`h-full transition-all duration-500 ${
                  buildStatus?.status === 'success' || buildStatus?.status === 'succeeded'
                    ? 'bg-green-500'
                    : buildStatus?.status === 'failed'
                    ? 'bg-red-500'
                    : 'bg-blue-500'
                } ${buildStatus?.status === 'running' ? 'animate-pulse' : ''}`}
                style={{ width: `${getProgressPercentage()}%` }}
              />
            </div>
          </div>

          {/* Status Info */}
          {buildStatus && (
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              <div className="bg-surface-3 rounded-lg p-3">
                <div className="text-xs text-text-tertiary mb-1">Statut</div>
                <div className="text-sm font-medium text-text-primary">
                  {getStatusText(buildStatus.status)}
                </div>
              </div>

              {/* Always show duration (real-time if running) */}
              <div className="bg-surface-3 rounded-lg p-3">
                <div className="text-xs text-text-tertiary mb-1">Durée</div>
                <div className="text-sm font-medium text-text-primary flex items-center">
                  {buildStatus.status === 'running' && (
                    <span className="w-2 h-2 bg-blue-500 rounded-full animate-pulse mr-2" />
                  )}
                  {formatDuration(elapsedTime)}
                </div>
              </div>

              {buildStatus.image_tag && (
                <div className="bg-surface-3 rounded-lg p-3">
                  <div className="text-xs text-text-tertiary mb-1">Image Tag</div>
                  <div className="text-sm font-medium text-text-primary font-mono">
                    {buildStatus.image_tag}
                  </div>
                </div>
              )}

              {(getStartTime() || buildStatus.created_at) && (
                <div className="bg-surface-3 rounded-lg p-3">
                  <div className="text-xs text-text-tertiary mb-1">Démarré</div>
                  <div className="text-sm font-medium text-text-primary">
                    {new Date(getStartTime() || buildStatus.created_at).toLocaleTimeString('fr-FR')}
                  </div>
                </div>
              )}
            </div>
          )}

          {/* Pipeline Tasks - Clickable tabs */}
          {buildStatus?.tasks && buildStatus.tasks.length > 0 && (
            <div>
              <h4 className="text-sm font-medium text-text-secondary mb-3">Pipeline</h4>
              <div className="flex flex-wrap gap-2">
                <button
                  onClick={() => { setActiveTab(null); setScanResults(null); }}
                  className={`flex items-center space-x-2 px-3 py-2 rounded-lg border cursor-pointer transition-colors ${
                    activeTab === null ? 'bg-accent-500/20 border-accent-400 ring-1 ring-accent-400' : 'bg-surface-3 border-border-1 hover:bg-surface-4'
                  }`}
                >
                  <span className="text-sm font-medium text-text-primary">Tous</span>
                </button>
                {buildStatus.tasks.map((task, index) => {
                  const taskLabel = formatTaskName(task.name);
                  const isScan = taskLabel.toLowerCase().includes('security') || taskLabel.toLowerCase().includes('scan');
                  const isActive = activeTab === taskLabel.toLowerCase();
                  return (
                    <button
                      key={index}
                      onClick={async () => {
                        setActiveTab(taskLabel.toLowerCase());
                        if (isScan && buildId) {
                          try {
                            const results = await apiService.getBuildScanResults(buildId);
                            setScanResults(results);
                          } catch { /* don't cache failed results */ }
                        }
                      }}
                      className={`flex items-center space-x-2 px-3 py-2 rounded-lg border cursor-pointer transition-colors ${
                        isActive ? 'bg-accent-500/20 border-accent-400 ring-1 ring-accent-400'
                        : task.status === 'succeeded' ? 'bg-green-500/12 border-green-500/30 hover:bg-green-500/20'
                        : task.status === 'failed' ? 'bg-red-500/12 border-red-500/30 hover:bg-red-500/20'
                        : task.status === 'running' ? 'bg-blue-500/12 border-blue-500/30'
                        : 'bg-surface-3 border-border-1 hover:bg-surface-4'
                      }`}
                    >
                      {getStatusIcon(task.status)}
                      <span className="text-sm font-medium text-text-primary">{taskLabel}</span>
                    </button>
                  );
                })}
                {buildStatus.scan_available === false && (
                  <div
                    className="flex items-center space-x-2 px-3 py-2 rounded-lg border border-border-1 bg-surface-3/50 cursor-default"
                    title="Les buildpacks construisent l'image sans etape de scan. Une analyse de vulnerabilite demande un build par Dockerfile."
                  >
                    <span className="text-sm text-text-tertiary">
                      Vulnerabilites : non analysees (buildpack)
                    </span>
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Scan Results Panel (shown when scan tab is active) */}
          {activeTab && (activeTab.includes('security') || activeTab.includes('scan')) && scanResults && (
            <div className="bg-surface-3 rounded-lg p-4 border border-border-1">
              <div className="flex items-center justify-between mb-3">
                <h4 className="text-sm font-medium text-text-primary">Vulnerabilites detectees</h4>
                <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${
                  scanResults.vulnerabilities === 0 ? 'bg-green-500/12 text-green-400'
                  : scanResults.cves.some((c: any) => c.s === 'CRITICAL') ? 'bg-red-500/12 text-red-400'
                  : 'bg-yellow-500/12 text-yellow-400'
                }`}>
                  {scanResults.vulnerabilities} vulnerabilite{scanResults.vulnerabilities !== 1 ? 's' : ''}
                </span>
              </div>
              {scanResults.cves.length > 0 ? (
                <div className="max-h-72 overflow-y-auto">
                  <table className="w-full text-xs">
                    <thead className="sticky top-0 bg-surface-3">
                      <tr className="border-b border-border-1 text-text-tertiary">
                        <th className="text-left py-2 pr-2 font-medium">Severite</th>
                        <th className="text-left py-2 pr-2 font-medium">CVE</th>
                        <th className="text-left py-2 pr-2 font-medium">Package</th>
                        <th className="text-left py-2 font-medium">Version fix</th>
                      </tr>
                    </thead>
                    <tbody>
                      {scanResults.cves.map((cve: any, i: number) => (
                        <tr key={i} className="border-b border-border-0">
                          <td className="py-1.5 pr-2">
                            <span className={`px-1.5 py-0.5 rounded font-medium whitespace-nowrap ${
                              cve.s === 'CRITICAL' ? 'bg-red-500/20 text-red-400'
                              : cve.s === 'HIGH' ? 'bg-orange-500/20 text-orange-400'
                              : cve.s === 'MEDIUM' ? 'bg-yellow-500/20 text-yellow-400'
                              : 'bg-gray-500/12 text-gray-400'
                            }`}>{cve.s}</span>
                          </td>
                          <td className="py-1.5 pr-2 font-mono text-accent-400">{cve.id}</td>
                          <td className="py-1.5 pr-2 text-text-secondary">{cve.pkg}</td>
                          <td className="py-1.5 text-green-400">{cve.fix || '-'}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              ) : (
                <p className="text-xs text-green-400">Aucune vulnerabilite detectee</p>
              )}
            </div>
          )}

          {/* Stages (fallback for non-Tekton builds) - Horizontal display */}
          {buildStatus?.stages && buildStatus.stages.length > 0 && (!buildStatus?.tasks || buildStatus.tasks.length === 0) && (
            <div>
              <h4 className="text-sm font-medium text-text-secondary mb-3">Étapes du build</h4>
              <div className="flex flex-wrap gap-2">
                {buildStatus.stages.map((stage, index) => (
                  <div
                    key={index}
                    className={`flex items-center space-x-2 px-3 py-2 rounded-lg border ${
                      stage.status === 'succeeded' || stage.status === 'success'
                        ? 'bg-green-500/12 border-green-500/30'
                        : stage.status === 'failed'
                        ? 'bg-red-500/12 border-red-500/30'
                        : stage.status === 'running'
                        ? 'bg-blue-500/12 border-blue-500/30'
                        : 'bg-surface-3 border-border-1'
                    }`}
                  >
                    {getStatusIcon(stage.status)}
                    <span className="text-sm font-medium text-text-primary">{formatTaskName(stage.name)}</span>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Error Message */}
          {buildStatus?.status === 'failed' && buildStatus.error_message && (
            <div className="p-4 bg-red-500/12 border border-red-500/30 rounded-lg">
              <div className="flex items-start">
                <XCircleIcon className="w-6 h-6 text-red-500 mr-3 flex-shrink-0" />
                <div>
                  <h4 className="text-sm font-medium text-red-400 mb-1">Erreur de build</h4>
                  <p className="text-xs text-red-400 font-mono">{buildStatus.error_message}</p>
                </div>
              </div>
            </div>
          )}

          {/* Success Message */}
          {(buildStatus?.status === 'success' || buildStatus?.status === 'succeeded') && buildStatus.image_url && (
            <div className="p-4 bg-green-500/12 border border-green-500/30 rounded-lg">
              <div className="flex items-start">
                <CheckCircleIcon className="w-6 h-6 text-green-500 mr-3 flex-shrink-0" />
                <div className="flex-1">
                  <h4 className="text-sm font-medium text-green-400 mb-2">Build réussi !</h4>
                  <p className="text-xs text-green-400 mb-2">
                    L'image Docker a été construite et poussée avec succès vers le registry.
                  </p>
                  <div className="bg-surface-2 p-2 rounded border border-green-500/30">
                    <p className="text-xs font-mono text-text-secondary break-all">
                      {buildStatus.image_url}:{buildStatus.image_tag}
                    </p>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Logs Section - Always displayed */}
          <div>
            <div className="flex justify-between items-center mb-3">
              <h4 className="text-sm font-medium text-text-secondary flex items-center"><ClipboardDocumentListIcon className="w-4 h-4 inline mr-1" /> Logs du build (en temps réel)</h4>
              <button
                onClick={() => setShowLogs(!showLogs)}
                className="text-sm text-accent-400 hover:text-accent-300 font-medium"
              >
                {showLogs ? <><ChevronUpIcon className="w-4 h-4 inline mr-1" /> Masquer</> : <><ChevronDownIcon className="w-4 h-4 inline mr-1" /> Afficher</>}
              </button>
            </div>

            {showLogs && (
              <div className="bg-gray-900 rounded-lg p-4 font-mono text-xs border border-gray-700">
                <pre className="text-green-400 whitespace-pre-wrap break-words">
                  {logs || <span className="flex items-center"><ClockIcon className="w-4 h-4 inline mr-1" /> Chargement des logs...</span>}
                </pre>
              </div>
            )}
          </div>
        </div>

        {/* Footer */}
        <div className="flex justify-between items-center p-6 border-t border-border-1">
          <div className="flex space-x-3">
            {/* Delete button - show for all non-success builds */}
            {buildStatus?.status !== 'success' && buildStatus?.status !== 'succeeded' && (
              <button
                onClick={handleDeleteBuild}
                disabled={isDeleting}
                className="px-6 py-2 bg-red-600 text-white rounded-md hover:bg-red-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center"
              >
                {isDeleting ? (
                  <>
                    <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-white" fill="none" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                    </svg>
                    Suppression...
                  </>
                ) : (
                  <>
                    <TrashIcon className="w-4 h-4 inline mr-1" /> Supprimer
                  </>
                )}
              </button>
            )}
          </div>

          <div className="flex space-x-3">
            {/* Retry button - show for failed AND cancelled builds */}
            {(buildStatus?.status === 'failed' || buildStatus?.status === 'cancelled') && (
              <button
                onClick={handleRetryBuild}
                disabled={isRetrying}
                className="px-6 py-2 bg-accent-600 text-white rounded-md hover:bg-accent-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center"
              >
                {isRetrying ? (
                  <>
                    <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-white" fill="none" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                    </svg>
                    Relance...
                  </>
                ) : (
                  <>
                    <ArrowPathIcon className="w-4 h-4 inline mr-1" /> Relancer
                  </>
                )}
              </button>
            )}

            {/* Close button */}
            <button
              onClick={onClose}
              className="px-6 py-2 bg-surface-4 text-text-secondary rounded-md hover:bg-surface-4 transition-colors"
              title={buildStatus?.status === 'running' ? 'Fermer (le build continuera en arrière-plan)' : 'Fermer'}
            >
              Fermer
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default BuildProgressModal;
